import { useState } from "react";
import { formatKstStamp, normalizeSerial } from "@rma-orbit/shared";
import { api } from "../api/client.js";
import { navigate } from "../App.js";
import { Busy, Cell, ErrorNotice, Notice, useAsync } from "../lib/ui.js";
import { downloadRowsAsXlsx } from "../lib/excel.js";

/**
 * 장비(시리얼) 화면 — 시리얼 하나로 전 생애 조회 (harness/UI-SPEC.md §1).
 * 이 프로젝트의 제1 목적이 여기서 눈에 보인다: 교체로 시리얼이 바뀌어도 이력이
 * 끊기지 않는다.
 */

const KIND_LABEL: Record<string, string> = {
  CASE_RECEIVED: "입고",
  STATUS_CHANGE: "상태",
  SWAP: "교체",
  SHIPMENT: "출고",
  EVIDENCE: "증적",
  NOTE: "메모",
};

export function Devices({ serial, can }: { serial?: string; can: (p: string) => boolean }) {
  const [input, setInput] = useState(serial ?? "");
  const preview = normalizeSerial(input).norm;

  const timeline = useAsync(
    () => (serial ? api.device(serial) : Promise.resolve(null)),
    [serial ?? ""],
  );

  const [note, setNote] = useState("");
  const [noteCategory, setNoteCategory] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function addNote() {
    if (!serial || note.trim() === "") return;
    setBusy(true);
    setError(null);
    try {
      await api.addNote(serial, note.trim(), noteCategory.trim() || undefined);
      setNote("");
      setNoteCategory("");
      timeline.reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function download() {
    if (!timeline.data) return;
    const rows = timeline.data.events.map((e) => ({
      일시: e.at ?? "",
      종류: KIND_LABEL[e.kind] ?? e.kind,
      연결시리얼: e.related_serial ?? "",
      케이스: e.case_id ?? "",
      내용: e.summary,
    }));
    await downloadRowsAsXlsx(rows, `장비_${timeline.data.device.serial_norm}.xlsx`, "이력");
  }

  return (
    <>
      <h1>장비(시리얼)</h1>
      <p className="page-desc">
        시리얼 하나로 전 이력을 봅니다. 교체로 시리얼이 바뀌어도 양쪽에서 같은 교체 사건이
        보입니다.
      </p>

      <div className="card">
        <div className="row">
          <label className="field grow">
            시리얼
            <input
              value={input}
              placeholder="전체를 정확히 — 앞뒤 공백·소문자는 그대로 넣어도 됩니다"
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && preview) navigate(`#/devices/${encodeURIComponent(preview)}`);
              }}
            />
          </label>
          <button
            className="primary"
            disabled={preview === ""}
            onClick={() => navigate(`#/devices/${encodeURIComponent(preview)}`)}
          >
            조회
          </button>
        </div>
        {input !== "" && preview !== input && (
          <p className="muted" style={{ marginBottom: 0 }}>
            이렇게 찾습니다: <span className="mono">{preview}</span>
          </p>
        )}
      </div>

      {!serial && <Notice>조회할 시리얼을 넣어 주세요.</Notice>}

      {serial && timeline.loading && <Busy />}
      {serial && timeline.error && <ErrorNotice error={timeline.error} />}

      {timeline.data && (
        <>
          <div className="card">
            <h2>
              장비
              <span className="sub mono">{timeline.data.device.serial_norm}</span>
            </h2>
            <div className="row" style={{ gap: 24 }}>
              <div>
                <div className="muted">원본 표기</div>
                <div className="mono">{timeline.data.device.serial_raw}</div>
              </div>
              <div>
                <div className="muted">모델</div>
                <div>{timeline.data.device.model}</div>
              </div>
              <div>
                <div className="muted">위치</div>
                <div>
                  <Cell value={timeline.data.device.location} />
                </div>
              </div>
              <div>
                <div className="muted">처음 본 날</div>
                <div className="num">
                  <Cell value={timeline.data.device.first_seen_at} />
                </div>
              </div>
              <div>
                <div className="muted">마지막 본 날</div>
                <div className="num">
                  <Cell value={timeline.data.device.last_seen_at} />
                </div>
              </div>
              <div>
                <div className="muted">펌웨어</div>
                <div>
                  <Cell value={timeline.data.device.firmware} />
                </div>
              </div>
            </div>
          </div>

          {/*
            ★ 이 프로젝트의 제1 목적이 이 줄이다 (harness/SOUL.md P1).
            타임라인 어딘가에 한 줄로 섞여 있으면, 손에 장비를 들고 있는 사람은
            그것을 못 찾는다. 연결이 있으면 **맨 위에** 말한다.
          */}
          {timeline.data.events
            .filter((e) => e.kind === "SWAP" && e.related_serial !== null)
            .map((e) => (
              <Notice kind="ok" key={`${e.ref_id}-${e.related_serial}`}>
                {/* 방향은 서버가 만든 문장이 이미 말한다 — 화면이 글자를 뜯어 짐작하지 않는다 */}
                <strong>★ 교체로 이어진 장비</strong> — {e.summary.replace(/^교체 — /, "")}
                <br />
                짝이 되는 시리얼{" "}
                <a
                  href={`#/devices/${encodeURIComponent(e.related_serial!)}`}
                  onClick={(ev) => {
                    ev.preventDefault();
                    navigate(`#/devices/${encodeURIComponent(e.related_serial!)}`);
                  }}
                >
                  <strong>{e.related_serial}</strong>
                </a>
                {e.at !== null && <span className="muted"> · {formatKstStamp(e.at)}</span>}
              </Notice>
            ))}

          <div className="card">
            <h2>
              이력
              <span className="sub">{timeline.data.events.length}건</span>
            </h2>

            <div className="row" style={{ marginBottom: 12 }}>
              <div className="grow" />
              {can("export.excel") && <button onClick={download}>엑셀 다운로드</button>}
            </div>

            {timeline.data.events.length === 0 ? (
              <p className="muted">아직 기록이 없습니다.</p>
            ) : (
              <ul className="timeline">
                {timeline.data.events.map((event, i) => (
                  <li key={i}>
                    <span className="when">
                      {event.at === null ? <span className="empty">시각 모름</span> : formatKstStamp(event.at)}
                    </span>
                    <span>
                      <span className="badge">{KIND_LABEL[event.kind] ?? event.kind}</span>
                    </span>
                    <span>
                      {event.summary}
                      {event.related_serial !== null && (
                        <>
                          {" "}
                          <a
                            href={`#/devices/${encodeURIComponent(event.related_serial)}`}
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(`#/devices/${encodeURIComponent(event.related_serial!)}`);
                            }}
                          >
                            {event.related_serial} 보기
                          </a>
                        </>
                      )}
                      {event.case_id !== null && (
                        <>
                          {" "}
                          <a
                            href={`#/cases/${event.case_id}`}
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(`#/cases/${event.case_id}`);
                            }}
                          >
                            케이스 보기
                          </a>
                        </>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {can("case.write") && (
            <div className="card">
              <h2>
                메모<span className="sub">증적이 아니라 장비에 남는 사실을 적습니다</span>
              </h2>
              <ErrorNotice error={error} />
              <div className="row" style={{ alignItems: "stretch" }}>
                <label className="field" style={{ width: 160 }}>
                  분류 (선택)
                  <input
                    value={noteCategory}
                    placeholder="통화 / 지시 …"
                    onChange={(e) => setNoteCategory(e.target.value)}
                  />
                </label>
                <label className="field grow">
                  내용
                  <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
                </label>
              </div>
              <div className="row" style={{ marginTop: 8 }}>
                <button className="primary" disabled={busy || note.trim() === ""} onClick={addNote}>
                  {busy ? "저장 중…" : "메모 남기기"}
                </button>
                <span className="muted">메모는 지우는 경로가 없습니다 — 기록은 지우지 않습니다.</span>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}
